<template>
  <div class="mt-4 px-5">
    <v-alert dense text shaped type="info">
      <b>Arbeitseinsätze</b>
      <div>
        Hier können Sie Arbeitseinsätze planen und verwalten.
      </div>
    </v-alert>

    <v-data-table
      v-model="selected"
      :headers="headers"
      :items="$store.state.ClubApp.Arbeitseinsaetze"
      :loading="loading"
      show-select
      sort-by="geplant_von"
      class="elevation-1"
    >
      <template v-slot:top>
        <v-toolbar flat>
          <v-toolbar-title>Einsatzplanung</v-toolbar-title>
          <v-divider class="mx-4" inset vertical></v-divider>
          
          <v-menu offset-y v-if="selected.length > 0">
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                color="secondary"
                dark
                class="mr-2"
                v-bind="attrs"
                v-on="on"
              >
                Aktionen ({{ selected.length }})
                <v-icon right>mdi-chevron-down</v-icon>
              </v-btn>
            </template>
            <v-list>
              <v-subheader>Status ändern</v-subheader>
              <v-list-item v-for="status in statusOptions" :key="status" @click="bulkStatusUpdate(status)">
                <v-list-item-title>{{ status }}</v-list-item-title>
              </v-list-item>
              <v-divider></v-divider>
              <v-list-item @click="bulkDelete">
                <v-list-item-title class="error--text">Löschen</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>

          <v-spacer></v-spacer>
          <v-btn color="primary" dark @click="openCreateDialog">
            Neuer Einsatz
          </v-btn>
          <v-btn icon @click="fetchData" :loading="loading">
            <v-icon>mdi-reload</v-icon>
          </v-btn>
        </v-toolbar>
      </template>

      <template v-slot:item.teilnehmer_count="{ item }">
        <v-btn
          v-if="item.teilnehmer_count > 0"
          small
          outlined
          color="primary"
          @click="showResponses(item)"
          class="rounded-pill"
        >
          <v-icon left small>mdi-account-group</v-icon>
          {{ item.teilnehmer_count }}
        </v-btn>
        <v-tooltip v-else bottom text="Keine Teilnehmer angemeldet">
          <template v-slot:activator="{ on, attrs }">
            <span v-bind="attrs" v-on="on" class="grey--text lighten-1 ml-4">
              <v-icon small class="grey--text lighten-1">mdi-account-group-outline</v-icon>
              0
            </span>
          </template>
          <span>Keine Teilnehmer</span>
        </v-tooltip>
      </template>

      <template v-slot:item.zeitraum="{ item }">
        {{ formatDateTime(item.geplant_von) }} - {{ formatDateTime(item.geplant_bis) }}
      </template>

      <template v-slot:item.status="{ item }">
        <v-chip small :color="getStatusColor(item.status)" dark>
          {{ item.status }}
        </v-chip>
      </template>

      <template v-slot:item.actions="{ item }">
        <div class="d-flex justify-center">
          <v-btn
            icon
            color="info"
            title="Bearbeiten"
            @click="editItem(item)"
          >
            <v-icon>mdi-pencil</v-icon>
          </v-btn>

          <v-btn
            icon
            color="success"
            title="Kopieren"
            @click="copyItem(item)"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>

          <v-btn
            icon
            color="primary"
            title="Einladungen"
            @click="showInvitePreview(item)"
          >
            <v-icon>mdi-email-send</v-icon>
          </v-btn>

          <v-btn
            icon
            color="error"
            title="Löschen"
            @click="deleteItem(item)"
          >
            <v-icon>mdi-trash-can</v-icon>
          </v-btn>
        </div>
      </template>
    </v-data-table>

    <!-- Invite Preview Modal -->
    <v-dialog v-model="previewDialog" max-width="800px">
      <v-card v-if="previewItem">
        <v-card-title class="headline blue darken-1 white--text">
          <v-icon left dark>mdi-email-search</v-icon>
          Vorschau: Einladung versenden
        </v-card-title>
        <v-card-text class="pt-6">
          <div v-if="previewLoading" class="text-center py-10">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
            <div class="mt-2">Vorschau wird geladen...</div>
          </div>
          <div v-else-if="previewData" class="email-preview border rounded pa-4 grey lighten-5">
            <div class="mb-4">
              <strong>An:</strong> Alle Vereinsmitglieder
            </div>
            <div class="mb-4">
              <strong>Betreff:</strong> {{ previewData.subject }}
            </div>
            <v-divider class="mb-4"></v-divider>
            <div class="email-container" style="height: 500px; overflow: hidden; border: 1px solid #ddd;">
              <iframe
                :srcdoc="previewData.body"
                style="width: 100%; height: 100%; border: none;"
                title="E-Mail Vorschau"
              ></iframe>
            </div>
          </div>
          <v-alert v-if="!previewLoading" type="info" class="mt-4" dense text>
            Dies ist die exakte Vorschau der E-Mail, wie sie an die Mitglieder versendet wird.
          </v-alert>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" text @click="previewDialog = false">Abbrechen</v-btn>
          <v-btn color="primary" :disabled="previewLoading" @click="confirmInvite(previewItem)">
            <v-icon left>mdi-send</v-icon>
            Einladungen versenden
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>


    <!-- Responses (Antworten) Modal -->
    <v-dialog v-model="responsesDialog" max-width="900px">
      <v-card v-if="selectedAssignment">
        <v-card-title class="headline blue darken-1 white--text">
          <v-icon left dark>mdi-account-group</v-icon>
          Teilnehmer & Antworten: {{ selectedAssignment.titel }}
        </v-card-title>
        <v-card-text class="pt-6">
          <div v-if="loadingResponses" class="text-center py-10">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
            <div class="mt-2">Daten werden geladen...</div>
          </div>
          <div v-else-if="responsesData">
            <v-row class="mb-4">
              <v-col cols="12" sm="3">
                <v-card color="success" dark>
                  <v-card-text class="text-center">
                    <div class="text-h4">{{ responsesData.zusammenfassung.angemeldet }}</div>
                    <div>Angemeldet</div>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12" sm="3">
                <v-card color="error" dark>
                  <v-card-text class="text-center">
                    <div class="text-h4">{{ responsesData.zusammenfassung.abgesagt }}</div>
                    <div>Abgesagt</div>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12" sm="3">
                <v-card color="info" dark>
                  <v-card-text class="text-center">
                    <div class="text-h4">{{ responsesData.zusammenfassung.teilgenommen }}</div>
                    <div>Teilgenommen</div>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12" sm="3">
                <v-card color="grey" dark>
                  <v-card-text class="text-center">
                    <div class="text-h4">{{ responsesData.zusammenfassung.keine_antwort }}</div>
                    <div>Keine Antwort</div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>

            <v-text-field
              v-model="responseSearch"
              append-icon="mdi-magnify"
              label="Suche"
              single-line
              hide-details
              class="mb-4"
            ></v-text-field>

            <v-data-table
              :headers="responseHeaders"
              :items="responsesData.antworten"
              :search="responseSearch"
              class="elevation-1"
              no-data-text="Keine Einträge gefunden"
            >
              <template v-slot:item.antwort="{ item }">
                <v-chip v-if="item.antwort" small :color="getStatusColor(item.antwort)" dark>
                  {{ item.antwort }}
                </v-chip>
                <v-chip v-else small color="grey" outlined>
                  Keine Antwort
                </v-chip>
              </template>
              <template v-slot:item.beantwortet_am="{ item }">
                <span v-if="item.antwort">
                  {{ formatDateTime(item.beantwortet_am) }}
                </span>
                <span v-else class="grey--text text-caption">
                  Eingeladen: {{ formatDateTime(item.beantwortet_am) }}
                </span>
              </template>
            </v-data-table>
          </div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="responsesDialog = false">Schließen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Conflict Dialog (Bereits versendet) -->
    <v-dialog v-model="conflictDialog" max-width="500px">
      <v-card v-if="conflictData">
        <v-card-title class="headline orange darken-2 white--text">
          <v-icon left dark>mdi-alert</v-icon>
          Bereits versendet
        </v-card-title>
        <v-card-text class="pt-6">
          <p>{{ conflictData.message }}</p>
          <v-list dense>
            <v-list-item>
              <v-list-item-content>
                <v-list-item-subtitle>Zuletzt gesendet am</v-list-item-subtitle>
                <v-list-item-title>{{ formatDateTime(conflictData.einladung_gestartet_at) }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-content>
                <v-list-item-subtitle>Status</v-list-item-subtitle>
                <v-list-item-title>
                  {{ conflictData.einladung_gesendet }} von {{ conflictData.einladung_gesamt }} versendet
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>

          <v-radio-group v-model="inviteModus" label="Versand-Modus">
            <v-radio label="Nur an Mitglieder ohne Antwort" value="ohne_antwort"></v-radio>
            <v-radio label="Alle erneut anschreiben" value="alle"></v-radio>
          </v-radio-group>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" text @click="conflictDialog = false">Abbrechen</v-btn>
          <v-btn color="orange darken-2" dark @click="reconfirmInvite">
            Trotzdem senden
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Mail Progress Overlay -->
    <v-dialog v-model="progressDialog" persistent max-width="400px">
      <v-card color="primary" dark>
        <v-card-text class="pt-4">
          <div class="text-h6 mb-2">Einladungen werden versendet...</div>
          <div v-if="mailStatus">
            <v-progress-linear
              :value="(mailStatus.gesendet / mailStatus.gesamt) * 100"
              color="white"
              height="25"
              striped
              reactive
            >
              <template v-slot:default="{ value }">
                <strong>{{ Math.ceil(value) }}%</strong>
              </template>
            </v-progress-linear>
            <div class="mt-2 d-flex justify-space-between">
              <span>{{ mailStatus.gesendet }} / {{ mailStatus.gesamt }} gesendet</span>
              <span v-if="mailStatus.fehlgeschlagen > 0" class="orange--text text--lighten-3">
                {{ mailStatus.fehlgeschlagen }} fehlgeschlagen
              </span>
            </div>
          </div>
          <div v-else class="text-center">
            <v-progress-circular indeterminate color="white" class="mr-2"></v-progress-circular>
            Initialisierung...
          </div>
        </v-card-text>
        <v-card-actions v-if="mailStatus && mailStatus.abgeschlossen">
          <v-spacer></v-spacer>
          <v-btn color="white" text @click="progressDialog = false">Schließen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Create/Edit Dialog -->
    <v-dialog v-model="dialog" max-width="600px">
      <v-card>
        <v-card-title>
          <span class="text-h5">{{ formTitle }}</span>
        </v-card-title>

        <v-card-text>
          <v-container>
            <v-row>
              <v-col cols="12">
                <v-text-field v-model="editedItem.titel" label="Titel"></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-textarea v-model="editedItem.beschreibung" label="Beschreibung" rows="2"></v-textarea>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field v-model="editedItem.ort" label="Ort"></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field v-model="editedItem.max_teilnehmer" label="Max. Teilnehmer" type="number"></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field v-model="editedItem.geplant_von" label="Von" type="datetime-local"></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field v-model="editedItem.geplant_bis" label="Bis" type="datetime-local"></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-select v-model="editedItem.status" :items="statusOptions" label="Status"></v-select>
              </v-col>
              <v-col cols="12" sm="6" v-if="editedIndex === -1 && !isCopy">
                <v-select v-model="editedItem.wiederholung_typ" :items="wiederholungOptions" label="Wiederholung"></v-select>
              </v-col>
              <v-col cols="12" sm="6" v-if="(editedIndex === -1 && !isCopy) && editedItem.wiederholung_typ !== 'einmalig'">
                <v-text-field v-model="editedItem.wiederholung_bis" label="Wiederholen bis" type="date"></v-text-field>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="close">Abbrechen</v-btn>
          <v-btn color="blue darken-1" text @click="save">Speichern</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import swal from "sweetalert2";

export default {
  name: "WorkinghoursAssignments",
  data() {
    return {
      loading: false,
      dialog: false,
      responsesDialog: false,
      conflictDialog: false,
      progressDialog: false,
      previewDialog: false,
      previewLoading: false,
      loadingResponses: false,
      responsesData: null,
      conflictData: null,
      mailStatus: null,
      pollingInterval: null,
      isCopy: false,
      inviteModus: "ohne_antwort",
      responseSearch: "",
      previewData: null,
      selected: [],
      selectedAssignment: null,
      previewItem: null,
      headers: [
        { text: "Titel", value: "titel" },
        { text: "Ort", value: "ort" },
        { text: "Zeitraum", value: "zeitraum", sortable: false },
        { text: "Teilnehmer", value: "teilnehmer_count" },
        { text: "Status", value: "status" },
        { text: "Aktionen", value: "actions", sortable: false, align: "center" },
      ],
      responseHeaders: [
        { text: "Mitgliedsnummer", value: "mitgliedsnummer" },
        { text: "Vorname", value: "vorname" },
        { text: "Name", value: "name" },
        { text: "Antwort", value: "antwort" },
        { text: "Beantwortet am", value: "beantwortet_am" },
      ],
      editedIndex: -1,
      editedItem: {
        titel: "",
        beschreibung: "",
        ort: "",
        max_teilnehmer: null,
        geplant_von: "",
        geplant_bis: "",
        status: "geplant",
        wiederholung_typ: "einmalig",
        wiederholung_bis: "",
      },
      defaultItem: {
        titel: "",
        beschreibung: "",
        ort: "",
        max_teilnehmer: null,
        geplant_von: "",
        geplant_bis: "",
        status: "geplant",
        wiederholung_typ: "einmalig",
        wiederholung_bis: "",
      },
      statusOptions: ["geplant", "aktiv", "abgeschlossen", "abgesagt"],
      wiederholungOptions: ["einmalig", "taeglich", "woechentlich", "monatlich"],
    };
  },
  computed: {
    formTitle() {
      return this.editedIndex === -1 ? "Neuer Arbeitseinsatz" : "Einsatz bearbeiten";
    },
  },
  mounted() {
    this.fetchData();
  },
  beforeDestroy() {
    this.stopPolling();
  },
  methods: {
    async fetchData() {
      this.loading = true;
      try {
        await this.$store.dispatch("ClubApp/getArbeitseinsaetze");
      } catch (error) {
        console.error("Fehler beim Laden der Arbeitseinsätze:", error);
      } finally {
        this.loading = false;
      }
    },
    formatDateTime(dateTimeStr) {
      if (!dateTimeStr) return "";
      // ISO Strings von Laravel (mit T und Z) korrekt parsen
      const date = new Date(dateTimeStr);
      if (isNaN(date.getTime())) {
        // Fallback für alte Formate falls vorhanden (z.B. "2026-05-04 12:25:00")
        const fallbackDate = new Date(dateTimeStr.replace(" ", "T"));
        if (isNaN(fallbackDate.getTime())) return dateTimeStr;
        return fallbackDate.toLocaleString([], {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });
      }
      return date.toLocaleString([], {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    getStatusColor(status) {
      switch (status) {
        case "geplant": return "info";
        case "aktiv": return "success";
        case "abgeschlossen": return "grey";
        case "abgesagt": return "error";
        case "angemeldet": return "success";
        case "teilgenommen": return "info";
        default: return "grey";
      }
    },
    openCreateDialog() {
      this.editedIndex = -1;
      this.isCopy = false;
      this.editedItem = Object.assign({}, this.defaultItem);
      this.dialog = true;
    },
    editItem(item) {
      this.editedIndex = this.$store.state.ClubApp.Arbeitseinsaetze.indexOf(item);
      this.isCopy = false;
      this.editedItem = Object.assign({}, item);
      // Wiederholungsdaten entfernen, da Editieren sie nicht unterstützt
      this.editedItem.wiederholung_typ = "einmalig";
      this.editedItem.wiederholung_bis = "";
      if (this.editedItem.geplant_von) {
        this.editedItem.geplant_von = new Date(this.editedItem.geplant_von).toLocaleString('sv').slice(0, 16).replace(' ', 'T');
      }
      if (this.editedItem.geplant_bis) {
        this.editedItem.geplant_bis = new Date(this.editedItem.geplant_bis).toLocaleString('sv').slice(0, 16).replace(' ', 'T');
      }
      this.dialog = true;
    },
    async copyItem(item) {
      try {
        const response = await this.$store.dispatch("ClubApp/copyArbeitseinsatz", item.id);
        const newEntry = response.data;
        if (newEntry && newEntry.id) {
          // Den neuen Eintrag öffnen und Titel anpassen
          this.editedIndex = -1; // Als neu behandeln für Save-Logik (POST statt PUT)
          this.isCopy = true;
          this.editedItem = Object.assign({}, newEntry);
          // Wiederholungsdaten entfernen, da Kopieren sie nicht unterstützt
          this.editedItem.wiederholung_typ = "einmalig";
          this.editedItem.wiederholung_bis = "";
          if (!this.editedItem.titel.startsWith("Kopie von")) {
            this.editedItem.titel = "Kopie von " + this.editedItem.titel;
          }
          if (this.editedItem.geplant_von) {
            this.editedItem.geplant_von = new Date(this.editedItem.geplant_von).toLocaleString('sv').slice(0, 16).replace(' ', 'T');
          }
          if (this.editedItem.geplant_bis) {
            this.editedItem.geplant_bis = new Date(this.editedItem.geplant_bis).toLocaleString('sv').slice(0, 16).replace(' ', 'T');
          }
          this.dialog = true;
        } else {
          this.fetchData();
        }
      } catch (error) {
        console.error("Fehler beim Kopieren:", error);
      }
    },
    async bulkStatusUpdate(status) {
      const ids = this.selected.map(i => i.id);
      try {
        await this.$store.dispatch("ClubApp/bulkStatusArbeitseinsaetze", { ids, status });
        this.selected = [];
        this.fetchData();
      } catch (error) {
        console.error("Fehler beim Bulk-Status-Update:", error);
      }
    },
    async bulkDelete() {
      if (confirm(`Möchten Sie ${this.selected.length} Arbeitseinsätze wirklich löschen?`)) {
        const ids = this.selected.map(i => i.id);
        try {
          await this.$store.dispatch("ClubApp/bulkDeleteArbeitseinsaetze", { ids });
          this.selected = [];
          this.fetchData();
        } catch (error) {
          console.error("Fehler beim Bulk-Löschen:", error);
        }
      }
    },
    async inviteMembers(item, modus = "ohne_antwort", bestaetigt = false) {
      try {
        const response = await this.$store.dispatch("ClubApp/inviteMembersArbeitseinsatz", { id: item.id, modus, bestaetigt });
        if (response.status === 200) {
          if (response.data.message === "Keine Empfänger gefunden.") {
             swal.fire("Info", response.data.message, "info");
          } else {
             this.startPolling(item.id);
          }
        }
      } catch (error) {
        if (error.response && error.response.status === 409) {
          this.conflictData = error.response.data;
          this.conflictDialog = true;
          this.previewItem = item;
        } else {
          console.error("Fehler beim Versenden der Einladungen:", error);
        }
      }
    },
    async reconfirmInvite() {
       this.conflictDialog = false;
       await this.inviteMembers(this.previewItem, this.inviteModus, true);
    },
    startPolling(id) {
       this.progressDialog = true;
       this.mailStatus = null;
       this.pollingInterval = setInterval(async () => {
          try {
             this.mailStatus = await this.$store.dispatch("ClubApp/getArbeitseinsatzMailStatus", id);
             if (this.mailStatus.abgeschlossen) {
                this.stopPolling();
             }
          } catch (e) {
             console.error("Polling error", e);
             this.stopPolling();
          }
       }, 2500);
    },
    stopPolling() {
       if (this.pollingInterval) {
          clearInterval(this.pollingInterval);
          this.pollingInterval = null;
       }
    },
    async showResponses(item) {
       this.selectedAssignment = item;
       this.responsesDialog = true;
       this.loadingResponses = true;
       this.responsesData = null;
       try {
          this.responsesData = await this.$store.dispatch("ClubApp/getArbeitseinsatzAntworten", item.id);
       } catch (error) {
          console.error("Fehler beim Laden der Antworten:", error);
       } finally {
          this.loadingResponses = false;
       }
    },
    async showInvitePreview(item) {
      this.previewItem = item;
      this.previewDialog = true;
      this.previewLoading = true;
      this.previewData = null;
      try {
        this.previewData = await this.$store.dispatch("ClubApp/getArbeitseinsatzMailPreview", item.id);
      } catch (error) {
        console.error("Fehler beim Laden der Mail-Vorschau:", error);
        this.previewDialog = false;
        swal.fire("Fehler", "Die Mail-Vorschau konnte nicht geladen werden.", "error");
      } finally {
        this.previewLoading = false;
      }
    },
    async confirmInvite(item) {
      this.previewDialog = false;
      const result = await swal.fire({
        title: "Einladungen versenden?",
        text: `Möchten Sie wirklich alle Vereinsmitglieder zu dem Arbeitseinsatz "${item.titel}" einladen?`,
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Ja, jetzt versenden!",
        cancelButtonText: "Abbrechen"
      });

      if (result.isConfirmed) {
        await this.inviteMembers(item);
      }
    },
    async deleteItem(item) {
      if (confirm("Möchten Sie diesen Arbeitseinsatz wirklich löschen?")) {
        try {
          await this.$store.dispatch("ClubApp/deleteArbeitseinsatz", item.id);
          this.fetchData();
        } catch (error) {
          console.error("Fehler beim Löschen:", error);
        }
      }
    },
    close() {
      this.dialog = false;
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem);
        this.editedIndex = -1;
        this.isCopy = false;
      });
    },
    async save() {
      const data = Object.assign({}, this.editedItem);
      if (data.geplant_von) data.geplant_von = data.geplant_von.replace("T", " ");
      if (data.geplant_bis) data.geplant_bis = data.geplant_bis.replace("T", " ");

      // Sicherstellen, dass Wiederholungsdaten nur bei echten neuen Einsätzen gesendet werden
      if (this.editedIndex !== -1 || this.isCopy) {
        data.wiederholung_typ = "einmalig";
        data.wiederholung_bis = "";
      }

      try {
        if (this.editedIndex > -1) {
          await this.$store.dispatch("ClubApp/updateArbeitseinsatz", { id: this.editedItem.id, data });
        } else {
          if (this.editedItem.id) {
             await this.$store.dispatch("ClubApp/updateArbeitseinsatz", { id: this.editedItem.id, data });
          } else {
             await this.$store.dispatch("ClubApp/createArbeitseinsatz", data);
          }
        }
        this.close();
        this.fetchData();
      } catch (error) {
        console.error("Fehler beim Speichern:", error);
      }
    },
  },
};
</script>
